import { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  Cog6ToothIcon,
  BellIcon,
  UserGroupIcon,
  SpeakerWaveIcon,
  LanguageIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import {
  updateAgency,
  listVoices,
  listAgencyMembers,
  inviteAgencyMember,
  removeAgencyMember,
} from '../services/api';
import { Voice, AgencyMember } from '../types';

// Language type and constants (US-only for now, but supporting English and Spanish)
type SupportedLanguage = 'en' | 'es';

const SUPPORTED_LANGUAGES: Record<SupportedLanguage, { name: string; nativeName: string }> = {
  en: { name: 'English', nativeName: 'English' },
  es: { name: 'Spanish', nativeName: 'Español' },
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

type TabId = 'profile' | 'defaults' | 'notifications' | 'team';

const tabs: { id: TabId; name: string; icon: React.ComponentType<any> }[] = [
  { id: 'profile', name: 'Agency Profile', icon: BuildingOfficeIcon },
  { id: 'defaults', name: 'Default Settings', icon: Cog6ToothIcon },
  { id: 'notifications', name: 'Notifications', icon: BellIcon },
  { id: 'team', name: 'Team', icon: UserGroupIcon },
];

export default function AgencySettingsPage() {
  const { currentAgency, refreshAgencies } = useAgency();
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">Select an agency to manage settings.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Agency Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage settings for {currentAgency.name}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={classNames(
                'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'profile' && (
          <ProfileSettings
            agency={currentAgency}
            onUpdate={refreshAgencies}
          />
        )}
        {activeTab === 'defaults' && (
          <DefaultSettings
            agency={currentAgency}
            onUpdate={refreshAgencies}
          />
        )}
        {activeTab === 'notifications' && (
          <NotificationSettings
            agency={currentAgency}
            onUpdate={refreshAgencies}
          />
        )}
        {activeTab === 'team' && (
          <TeamSettings agency={currentAgency} />
        )}
      </div>
    </div>
  );
}

function ProfileSettings({
  agency,
  onUpdate,
}: {
  agency: any;
  onUpdate: () => void;
}) {
  const [formData, setFormData] = useState({
    name: agency.name || '',
    slug: agency.slug || '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      await updateAgency(agency.id, formData);
      onUpdate();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating agency:', error);
      alert('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Agency Name
        </label>
        <input
          type="text"
          required
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500">
          The display name for your agency
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Agency Slug
        </label>
        <div className="flex rounded-md shadow-sm">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
            bryyx.com/
          </span>
          <input
            type="text"
            required
            pattern="[a-z0-9-]+"
            className="flex-1 min-w-0 block w-full rounded-none rounded-r-md border-gray-300 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.slug}
            onChange={(e) =>
              setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })
            }
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          URL-friendly identifier (lowercase letters, numbers, and hyphens only)
        </p>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        {success && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            Saved successfully
          </span>
        )}
      </div>
    </form>
  );
}

function DefaultSettings({
  agency,
  onUpdate,
}: {
  agency: any;
  onUpdate: () => void;
}) {
  const [formData, setFormData] = useState({
    defaultTimezone: agency.settings?.defaultTimezone || 'America/New_York',
    defaultVoiceId: agency.settings?.defaultVoiceId || '',
    defaultLanguage: (agency.localization?.language || 'en') as SupportedLanguage,
  });
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const data = await listVoices();
        setVoices(data);
      } catch (error) {
        console.error('Error fetching voices:', error);
      } finally {
        setLoadingVoices(false);
      }
    };
    fetchVoices();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      await updateAgency(agency.id, {
        settings: {
          ...agency.settings,
          defaultTimezone: formData.defaultTimezone,
          defaultVoiceId: formData.defaultVoiceId,
        },
        localization: {
          country: 'US',
          currency: 'usd',
          language: formData.defaultLanguage,
        },
      });
      onUpdate();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Default Timezone
        </label>
        <select
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.defaultTimezone}
          onChange={(e) => setFormData({ ...formData, defaultTimezone: e.target.value })}
        >
          <option value="America/New_York">Eastern Time (ET)</option>
          <option value="America/Chicago">Central Time (CT)</option>
          <option value="America/Denver">Mountain Time (MT)</option>
          <option value="America/Los_Angeles">Pacific Time (PT)</option>
          <option value="America/Anchorage">Alaska Time (AKT)</option>
          <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Default timezone for new client businesses
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <LanguageIcon className="inline-block h-4 w-4 mr-1" />
          Default AI Language
        </label>
        <select
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.defaultLanguage}
          onChange={(e) => setFormData({ ...formData, defaultLanguage: e.target.value as SupportedLanguage })}
        >
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, config]) => (
            <option key={code} value={code}>
              {config.name} ({config.nativeName})
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Default language for AI receptionist (can be changed per client)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <SpeakerWaveIcon className="inline-block h-4 w-4 mr-1" />
          Default AI Voice
        </label>
        {loadingVoices ? (
          <p className="text-sm text-gray-500">Loading voices...</p>
        ) : (
          <select
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.defaultVoiceId}
            onChange={(e) => setFormData({ ...formData, defaultVoiceId: e.target.value })}
          >
            <option value="">Select a voice...</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name}
              </option>
            ))}
          </select>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Default Retell voice for new client businesses
        </p>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        {success && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            Saved successfully
          </span>
        )}
      </div>
    </form>
  );
}

function NotificationSettings({
  agency,
  onUpdate,
}: {
  agency: any;
  onUpdate: () => void;
}) {
  const [formData, setFormData] = useState({
    emailOnNewLead: agency.settings?.emailOnNewLead ?? true,
    emailOnNewAppointment: agency.settings?.emailOnNewAppointment ?? true,
    emailOnMissedCall: agency.settings?.emailOnMissedCall ?? false,
    dailyDigest: agency.settings?.dailyDigest ?? false,
    notificationEmail: agency.settings?.notificationEmail || '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      await updateAgency(agency.id, {
        settings: {
          ...agency.settings,
          ...formData,
        },
      });
      onUpdate();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notification Email
        </label>
        <input
          type="email"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          placeholder="notifications@yourcompany.com"
          value={formData.notificationEmail}
          onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500">
          Email address for receiving notifications (leave blank to use account email)
        </p>
      </div>

      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 mt-1 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            checked={formData.emailOnNewLead}
            onChange={(e) => setFormData({ ...formData, emailOnNewLead: e.target.checked })}
          />
          <div>
            <span className="text-sm font-medium text-gray-700">New Lead Alert</span>
            <p className="text-xs text-gray-500">Get notified when a new lead is captured</p>
          </div>
        </label>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 mt-1 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            checked={formData.emailOnNewAppointment}
            onChange={(e) => setFormData({ ...formData, emailOnNewAppointment: e.target.checked })}
          />
          <div>
            <span className="text-sm font-medium text-gray-700">New Appointment Alert</span>
            <p className="text-xs text-gray-500">Get notified when an appointment is booked</p>
          </div>
        </label>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 mt-1 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            checked={formData.emailOnMissedCall}
            onChange={(e) => setFormData({ ...formData, emailOnMissedCall: e.target.checked })}
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Missed Call Alert</span>
            <p className="text-xs text-gray-500">Get notified when a call is abandoned or fails</p>
          </div>
        </label>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 mt-1 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            checked={formData.dailyDigest}
            onChange={(e) => setFormData({ ...formData, dailyDigest: e.target.checked })}
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Daily Digest</span>
            <p className="text-xs text-gray-500">Receive a daily summary of all activity</p>
          </div>
        </label>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        {success && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            Saved successfully
          </span>
        )}
      </div>
    </form>
  );
}

function TeamSettings({ agency }: { agency: any }) {
  const [members, setMembers] = useState<AgencyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await listAgencyMembers(agency.id);
        setMembers(data || []);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [agency.id]);

  const handleRemove = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      await removeAgencyMember(agency.id, memberId);
      setMembers(members.filter((m) => m.id !== memberId));
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  const handleAddMember = async (email: string, role: string) => {
    try {
      const newMember = await inviteAgencyMember(agency.id, email, role as 'admin' | 'viewer');
      setMembers([...members, newMember]);
      setShowAddModal(false);
    } catch (error: any) {
      console.error('Error adding member:', error);
      alert(error.message || 'Failed to add member');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 max-w-xl">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
          <p className="text-sm text-gray-500">
            People who can access this agency's dashboard
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
          Add Member
        </button>
      </div>

      <div className="space-y-3">
        {members.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No team members yet</p>
          </div>
        ) : (
          members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{member.name}</p>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={classNames(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    member.role === 'owner'
                      ? 'bg-purple-100 text-purple-800'
                      : member.role === 'admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  )}
                >
                  {member.role}
                </span>
                {member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemove(member.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Remove member"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddMember}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (email: string, role: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onAdd(email, role);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                User must have an existing account
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="viewer">Viewer - Read-only access</option>
                <option value="admin">Admin - Full access</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
